import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, ShieldCheck, Menu, X, Utensils, Tag, Star, Info, Phone, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const Navbar = () => {
  const { getItemCount } = useCart();
  const location = useLocation();
  const itemCount = getItemCount();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Don't show customer navbar on admin pages
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navLinks = [
    { to: '/menu', label: 'Menu', icon: Utensils },
    { to: '/deals', label: 'Deals', icon: Tag },
    { to: '/reviews', label: 'Reviews', icon: Star },
    { to: '/track-order', label: 'Track Order', icon: Package },
    { to: '/about', label: 'About', icon: Info },
    { to: '/contact', label: 'Contact', icon: Phone },
  ];

  return (
    <motion.nav
      initial={{ y: -100, x: "-50%", opacity: 0 }}
      animate={{
        y: 0,
        x: "-50%",
        opacity: 1,
        borderRadius: isMobileMenuOpen ? "1.5rem" : "9999px"
      }}
      className={cn(
        "floating-nav transition-all duration-300",
        isMobileMenuOpen ? "bg-black/90 backdrop-blur-3xl" : "bg-black/40 backdrop-blur-2xl"
      )}
    >
      <div className="px-6 md:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative w-10 h-10 md:w-12 md:h-12">
              <div className="absolute inset-0 bg-gold-500 rounded-full blur opacity-20 group-hover:opacity-40 transition-opacity duration-300 animate-pulse-slow"></div>
              <img
                src="/logo.jpg"
                alt="Magic World Logo"
                className="w-full h-full object-cover rounded-full border-2 border-gold-500 relative z-10 transition-transform duration-500 group-hover:rotate-12"
              />
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-300 via-gold-500 to-gold-300 font-serif tracking-wide animate-shimmer bg-[length:200%_auto]">
                Magic World
              </h1>
              <span className="text-[10px] md:text-xs text-gold-400/80 tracking-[0.3em] uppercase hidden sm:block font-light">
                Premium Cloud Kitchen
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={cn(
                  "px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 relative overflow-hidden group",
                  location.pathname === link.to
                    ? "text-black bg-gold-500 shadow-neon-gold font-bold"
                    : "text-gray-400 hover:text-gold-300 hover:bg-white/5"
                )}
              >
                <span className="relative z-10 flex items-center gap-2">
                  {link.label}
                </span>
                {location.pathname !== link.to && (
                  <div className="absolute inset-0 bg-gold-500/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                )}
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Admin Link */}
            <Link to="/admin/login">
              <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gold-400 hover:bg-gold-500/10 gap-2 rounded-full">
                <ShieldCheck className="w-4 h-4" />
                Admin
              </Button>
            </Link>

            {/* Cart Button */}
            <Link to="/cart">
              <Button
                variant="outline"
                className={cn(
                  "border-gold-500/30 text-gold-400 hover:bg-gold-500 hover:text-black hover:border-gold-500 transition-all duration-300 relative rounded-full w-12 h-12 p-0",
                  itemCount > 0 && "bg-gold-500/10 border-gold-500 shadow-[0_0_15px_rgba(234,179,8,0.3)]"
                )}
              >
                <ShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border-2 border-black"
                  >
                    {itemCount}
                  </motion.span>
                )}
              </Button>
            </Link>
          </div>

          {/* Mobile Actions */}
          <div className="flex items-center gap-3 lg:hidden">
            <Link to="/cart" className="relative group w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors">
              <ShoppingCart className="w-6 h-6 text-gold-400" />
              {itemCount > 0 && (
                <span className="absolute top-0 right-0 bg-red-600 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-black">
                  {itemCount}
                </span>
              )}
            </Link>

            <button
              onClick={toggleMobileMenu}
              className="text-gold-400 hover:text-gold-300 transition-colors p-2 rounded-full hover:bg-white/5"
            >
              {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 pb-6 pt-2 flex flex-col gap-2">
              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-4 px-4 py-3 rounded-xl text-lg font-medium transition-all duration-300",
                      location.pathname === link.to
                        ? "bg-gold-500 text-black shadow-lg shadow-gold-500/20"
                        : "text-gray-400 hover:text-gold-300 hover:bg-white/5"
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    {link.label}
                  </Link>
                );
              })}

              <div className="h-px bg-white/10 my-2" />

              <Link
                to="/admin/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-4 px-4 py-3 rounded-xl text-lg font-medium text-gray-400 hover:text-gold-300 hover:bg-white/5 transition-all duration-300"
              >
                <ShieldCheck className="w-5 h-5" />
                Admin Access
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;

